'use client';

import { useId, useState } from 'react';
import { creditDisplayState, groupOwnedCoursePlans, type OwnedCourseCredit, type OwnedCoursePlan } from '@/lib/owned-course-plans';
import styles from './owned-course-plans.module.css';

function PlanRow({ plan, now, timezone, open, onToggle }: { plan: OwnedCoursePlan; now: number; timezone: string; open: boolean; onToggle: () => void }) {
  const panelId = useId();
  const date = (value: string) => new Date(value).toLocaleDateString('zh-TW', { timeZone: timezone });
  const expiries = [...new Set(plan.credits.map(credit => credit.validUntil))];
  const expiry = expiries.length === 1 ? (expiries[0] ? `有效至 ${date(expiries[0])}` : '無使用期限') : '各課程效期不同';
  return <article className={styles.plan}>
    <button type="button" className={styles.summary} aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
      <span className={styles.heading}><strong>{plan.name}</strong><span className={styles.toggle} aria-hidden="true"/></span>
      <span className={styles.meta}>{date(plan.createdAt)} 入帳 · {expiry}</span>
      <span className={styles.balances}>{plan.credits.map(credit => {
        const state = creditDisplayState(credit, now);
        return <span className={styles.balance} key={credit.id}>
          <span>{credit.courseName}</span>
          <span className={styles.counts}>
            {state === '可使用' ? <strong>可用 {credit.remainingCredits} 堂</strong> : <strong className={styles.inactive}>{state}</strong>}
            {credit.reservedCredits > 0 && <span>已保留 {credit.reservedCredits} 堂</span>}
          </span>
        </span>;
      })}</span>
    </button>
    <div id={panelId} className={styles.details} hidden={!open}>
      {plan.credits.map(credit => <section className={styles.course} key={credit.id}>
        <div className={styles.courseHeading}><h4>{credit.courseName}</h4><span>{creditDisplayState(credit, now)}</span></div>
        <p>{credit.venueName}</p>
        <div className={styles.stampCaption}><strong>已使用 {credit.usedCredits} / {credit.totalCredits} 堂</strong><span>已預約 {credit.reservedCredits} 堂</span></div>
        <ol className={styles.stampGrid} aria-label={`${credit.courseName}堂數使用紀錄`}>
          {Array.from({ length: Math.max(credit.totalCredits, credit.usedCredits + credit.reservedCredits + credit.remainingCredits) }, (_, index) => {
            const used = index < credit.usedCredits;
            const reserved = !used && index < credit.usedCredits + credit.reservedCredits;
            const available = !used && !reserved && index < credit.usedCredits + credit.reservedCredits + credit.remainingCredits && creditDisplayState(credit, now) === '可使用';
            const label = used ? '已使用' : reserved ? '已預約' : available ? '未使用' : '不可用';
            return <li key={index} className={`${styles.stampCell} ${used ? styles.used : reserved ? styles.reserved : !available ? styles.unavailable : ''}`} aria-label={`第 ${index + 1} 堂：${label}`}>
              <span className={styles.cellNumber} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              {used ? <span className={styles.stamp} aria-hidden="true"><span>✓</span><b>已使用</b></span>
                : <span className={styles.cellLabel} aria-hidden="true">{reserved ? '已預約' : available ? '未使用' : '不可用'}</span>}
            </li>;
          })}
        </ol>
        <p>{date(credit.validFrom)} 起 · {credit.validUntil ? `有效至 ${date(credit.validUntil)}` : '無使用期限'}</p>
      </section>)}
      {plan.credits.length > 1 && <p className={styles.note}>各課程堂數分別計算，預約時依適用課程使用。</p>}
    </div>
  </article>;
}

function PlanList({ plans, now, timezone, openPlanId, onToggle }: { plans: OwnedCoursePlan[]; now: number; timezone: string; openPlanId: string | null; onToggle: (id: string) => void }) {
  const [visibleCount, setVisibleCount] = useState(5);
  return <>
    <div className={styles.list}>{plans.slice(0, visibleCount).map(plan => <PlanRow key={plan.id} plan={plan} now={now} timezone={timezone} open={openPlanId === plan.id} onToggle={() => onToggle(plan.id)}/>)}</div>
    {plans.length > visibleCount && <button type="button" className={styles.more} onClick={() => setVisibleCount(count => count + 5)}>再顯示 {Math.min(5, plans.length - visibleCount)} 筆（尚有 {plans.length - visibleCount} 筆）</button>}
  </>;
}

export function OwnedCoursePlans({ credits, now, timezone }: { credits: OwnedCourseCredit[]; now: number; timezone: string }) {
  const { current, history } = groupOwnedCoursePlans(credits, now);
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const togglePlan = (id: string) => setOpenPlanId(currentId => currentId === id ? null : id);
  return <section className={styles.root} aria-label="已持有的課程方案">
    <div className={styles.listHeading}><h3>目前方案 <span>{current.length}</span></h3><p>點開查看課程集點卡</p></div>
    {current.length ? <PlanList plans={current} now={now} timezone={timezone} openPlanId={openPlanId} onToggle={togglePlan}/> : <p className={styles.empty}>{history.length ? '目前沒有可使用或待上課的方案，過往紀錄保留在下方。' : '尚未擁有課程，購買後可在這裡查看堂數與效期。'}</p>}
    {history.length > 0 && <details className={styles.history}>
      <summary>歷史方案 <span>{history.length} 筆</span><small>已用完、已到期或已取消</small></summary>
      <PlanList plans={history} now={now} timezone={timezone} openPlanId={openPlanId} onToggle={togglePlan}/>
    </details>}
  </section>;
}
